import axios from "axios"
import {
  authorBaseUrl as baseUrl,
  publicApiBaseUrl,
  oktaTokenEndpoint,
  validateTokenEndPoint,
  SOURCE_ORIGIN,
} from "."

import Cookie from "universal-cookie"
import { store } from "@/store"
import { isAfterDate, getCurrentDate } from "@/utils/dateHelper"
import { setAuthData, setRevalidateTime } from "@/store/features/authSlice"
import {
  getAccessToken,
  getAnonymousId,
  addPageLoadAnalyticsEvent,
  revalidateTime,
} from "@/utils/helper"
import { getConfig } from "@/constants/config"
import { resolveHttpTimeoutMs } from "@/utils/httpTimeout"

const httpTimeoutMs = resolveHttpTimeoutMs(
  process.env.NEXT_PUBLIC_HTTP_TIMEOUT_MS,
  process.env.HTTP_TIMEOUT_MS
)
const aemAxiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: httpTimeoutMs,
  headers: {
    "Content-type": "application/json",
    KohlerTarget: "AzureApp",
  },
})

const apim = axios.create({
  timeout: 25000,
  headers: {
    "Content-type": "application/json",
    KohlerTarget: "AzureApp",
  },
})

let validateTokenRequest
let getAnonymousTokenRequest
let isRefreshingToken = false
let failedRequestsQueue = []

const validateToken = (accessToken, apimHost) => {
  if (!validateTokenRequest) {
    validateTokenRequest = axios.post(
      `${apimHost}${validateTokenEndPoint}`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    validateTokenRequest.then(
      resetValidateTokenRequest,
      resetValidateTokenRequest
    )
  }

  return validateTokenRequest
}

export const getAnonymousToken = async apimHost => {
  const cookie = new Cookie()
  const gId = cookie.get("commerce_gid") || getAccessToken().gId
  if (!getAnonymousTokenRequest) {
    const { apiEndPointData: { map: { channel } } = {} } = await getConfig()
    getAnonymousTokenRequest = axios.post(
      `${apimHost}${oktaTokenEndpoint}?scope=guest${
        channel ? `&channel=${channel}` : ""
      }`,
      null,
      {
        params: {
          gId: gId,
        },
      }
    )
    getAnonymousTokenRequest.then(
      resetGetAnonymousTokenRequest,
      resetGetAnonymousTokenRequest
    )
  }

  return getAnonymousTokenRequest
}

const processQueue = (error, token = null) => {
  const queue = [...failedRequestsQueue]
  failedRequestsQueue = []

  queue.forEach(promise => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })
}

const addRequestToQueue = originalRequest => {
  return new Promise((resolve, reject) => {
    failedRequestsQueue.push({
      resolve: token => {
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${token}`
        originalRequest._retry = true
        resolve(apim(originalRequest))
      },
      reject: err => {
        reject(err)
      },
    })
  })
}

const resetTokenRequests = () => {
  validateTokenRequest = null
  getAnonymousTokenRequest = null
}

const resetValidateTokenRequest = resetTokenRequests
const resetGetAnonymousTokenRequest = resetTokenRequests

apim.interceptors.request.use(
  async config => {
    config.baseURL = publicApiBaseUrl
    config.headers = config.headers || {}

    const { token: accessToken = "" } = getAccessToken()
    const anonymousId = getAnonymousId()

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    } else if (typeof window !== "undefined" && window.authAccessToken) {
      config.headers.Authorization = `Bearer ${window.authAccessToken}`
      window.authAccessToken = ""
    }

    if (!config.headers["X-Source-Origin"]) {
      config.headers["X-Source-Origin"] =
        SOURCE_ORIGIN ??
        (typeof window !== "undefined" ? window.location.origin : "")
    }

    const getNewToken = async () => {
      try {
        const res = await getAnonymousToken(publicApiBaseUrl)
        const { data: { access_token: accessToken = "" } = {} } = res
        const payload = {
          accessToken,
          revalidateTime: revalidateTime(),
        }
        store.dispatch(setAuthData(payload))
        config.headers.Authorization = `Bearer ${accessToken}`
        return config
      } catch (_error) {
        return Promise.reject(_error)
      }
    }

    addPageLoadAnalyticsEvent()
    const { auth: { revalidate_in: revalidateIn, isAuth = false } = {} } =
      store.getState() || {}

    const isAfter = isAfterDate(getCurrentDate(), revalidateIn)

    if (!accessToken) {
      return await getNewToken()
    } else if (anonymousId && isAfter && !isAuth) {
      try {
        const res = await validateToken(accessToken, publicApiBaseUrl)
        if (res.data?.valid) {
          store.dispatch(
            setRevalidateTime({ revalidateTime: revalidateTime() })
          )
          return config
        } else {
          return await getNewToken()
        }
      } catch {
        return await getNewToken()
      }
    } else {
      return config
    }
  },
  error => {
    return Promise.reject(error)
  }
)

apim.interceptors.response.use(
  async res => {
    return res
  },
  async error => {
    const originalConfig = error?.config || {}
    const isTokenEndpoint = originalConfig?.url?.includes(oktaTokenEndpoint)
    if (
      !store.getState()?.auth?.isAuth &&
      error?.response?.status === 401 &&
      !originalConfig._retry &&
      !isTokenEndpoint
    ) {
      originalConfig.headers = originalConfig.headers || {}

      // If token refresh is already in progress, queue this request
      if (isRefreshingToken) {
        return addRequestToQueue(originalConfig)
      }

      originalConfig._retry = true
      isRefreshingToken = true

      return getAnonymousToken(publicApiBaseUrl)
        .then(res => {
          const { data: { access_token: accessToken = "" } = {} } = res
          const payload = {
            accessToken,
            revalidateTime: revalidateTime(),
          }
          store.dispatch(setAuthData(payload))

          // Process all queued requests with new token
          processQueue(null, accessToken)

          originalConfig.headers.Authorization = `Bearer ${accessToken}`
          return apim(originalConfig)
        })
        .catch(_error => {
          processQueue(_error, null)
          return Promise.reject(_error)
        })
        .finally(() => {
          isRefreshingToken = false
        })
    }

    return Promise.reject(error)
  }
)

aemAxiosInstance.interceptors.request.use(async config => {
  const origin = SOURCE_ORIGIN ?? baseUrl
  if (!config.headers["X-Source-Origin"]) {
    if (config.headers?.common?.["Ocp-Apim-Subscription-Key"]) {
      config.headers["X-Source-Origin"] = origin
    }
  }
  return config
})

export { aemAxiosInstance, apim }
